/**
 * PackagesSeoMeta.js
 *
 * SEO + structured data component for the Packages (Pricing) section.
 * Mirrors the exact pattern of BlogSeoMeta.js / RogyogSeoMeta.js:
 *   - Keyed <Helmet> so React always re-renders on canonical change
 *   - trimDesc() respects word boundaries (155 char cap)
 *   - BreadcrumbList JSON-LD
 *   - ItemList JSON-LD listing all packages
 *   - Offer / Product JSON-LD for each pricing plan
 *   - Open Graph + Twitter Card
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";
const CANONICAL      = `${SITE_URL}/#packages`;

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Mirrors BlogSeoMeta — word-boundary aware trim
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// PackagesSeoMeta
//
// Props:
//   packages  {Array}  — the full yogaPackage.json array
// ═══════════════════════════════════════════════════════════════════════════════

export const PackagesSeoMeta = ({ packages = [] }) => {
  const title       = `Yoga Programs & Pricing | ${SITE_NAME}`;
  const description = trimDesc(
    "Explore our carefully curated yoga programs — from prenatal yoga to " +
    "therapeutic and foundational practices. Find the perfect program and start " +
    "your journey toward balance, strength, and inner peace."
  );
  const keywords =
    "yoga programs, yoga pricing, yoga classes, prenatal yoga, therapeutic yoga, " +
    "yoga plans, online yoga, yoga shastra";

  // ── BreadcrumbList ───────────────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",     item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Our Program", item: CANONICAL },
    ],
  };

  // ── ItemList — one entry per program ─────────────────────────────────────────
  const itemListSchema = {
    "@context":     "https://schema.org",
    "@type":        "ItemList",
    name:            "Yoga Programs",
    description,
    numberOfItems:   packages.length,
    itemListElement: packages.map((pkg, idx) => ({
      "@type":    "ListItem",
      position:   idx + 1,
      name:       pkg.name,
      url:        CANONICAL,
      image:      pkg.image?.startsWith("http") ? pkg.image : `${SITE_URL}${pkg.image}`,
    })),
  };

  // ── Product + Offer — one per pricing plan inside each package ───────────────
  const offerSchemas = packages.flatMap((pkg) =>
    (pkg.classArray ?? []).map((plan) => ({
      "@context": "https://schema.org",
      "@type":    "Product",
      name:        `${pkg.name} — ${plan.className}`,
      description: trimDesc(plan.classProperty?.replace(/--/g, ", ") ?? ""),
      image:       pkg.image?.startsWith("http") ? pkg.image : `${SITE_URL}${pkg.image}`,
      brand: {
        "@type": "Brand",
        name:    SITE_NAME,
      },
      offers: {
        "@type":       "Offer",
        name:           plan.className,
        price:          plan.classPrice,
        priceCurrency:  "INR",
        availability:   "https://schema.org/InStock",
        url:            CANONICAL,
        seller: {
          "@type": "Organization",
          name:    SITE_NAME,
          url:     SITE_URL,
        },
      },
    }))
  );

  // Keyed on canonical — mirrors BlogSeoMeta so Helmet flushes correctly
  return (
    <Helmet key={CANONICAL}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords"    content={keywords} />
      <link rel="canonical"    href={CANONICAL} />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={CANONICAL} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      {offerSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};
